/** #1544 — Remap generic "This computer — USB DAC" saved meta to local-native WASAPI. */
export type ZoneRow = {
  id: string;
  name?: string;
  type?: string;
  host?: string;
  capabilities?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

const GENERIC_USB_DAC_RE =
  /^this computer\s*[—–-]\s*usb dac$/i;

export function isGenericUsbDacDisplayName(name: string | null | undefined): boolean {
  const n = String(name || '').trim();
  if (!n) return false;
  return GENERIC_USB_DAC_RE.test(n);
}

function isLocalNativeUsb(zone: ZoneRow): boolean {
  return zone.type === 'local-native'
    || String(zone.id || '').startsWith('local-native:');
}

/** Prefer the connector-reported USB DAC over a stale saved placeholder. */
export function resolveGenericUsbDacToLocalNative(
  zones: ZoneRow[],
  selectedUsbDac?: { id?: string; name?: string } | null,
): ZoneRow | null {
  const natives = zones.filter(isLocalNativeUsb);
  if (!natives.length) return null;

  if (selectedUsbDac?.id) {
    const byAudioId = natives.find(
      (z) => String(z.metadata?.audio_device_id || '') === selectedUsbDac.id
        || z.id === selectedUsbDac.id,
    );
    if (byAudioId) return byAudioId;
  }

  if (selectedUsbDac?.name) {
    const key = selectedUsbDac.name.trim().toLowerCase();
    const byName = natives.find((z) => String(z.name || '').trim().toLowerCase() === key);
    if (byName) return byName;
  }

  const psAudio = natives.find((z) => /ps audio|extended resolution/i.test(String(z.name || '')));
  if (psAudio) return psAudio;

  return natives.length === 1 ? natives[0] : null;
}

export function remapSavedUsbDacZone(
  savedId: string,
  zones: ZoneRow[],
  selectedUsbDac?: { id?: string; name?: string } | null,
): ZoneRow | null {
  const metaName = (() => {
    try {
      const raw = localStorage.getItem('maestro_saved_zone_meta');
      if (!raw) return '';
      const parsed = JSON.parse(raw) as { id?: string; name?: string };
      if (String(parsed?.id || '') !== savedId) return '';
      return String(parsed.name || '');
    } catch {
      return '';
    }
  })();

  if (!isGenericUsbDacDisplayName(metaName)) return null;
  return resolveGenericUsbDacToLocalNative(zones, selectedUsbDac);
}
